/**
 * People → Talent Acquisition → Hiring Manager Intake: la entrada propia del
 * agente de intake en el menú (Sol 28/09: un agente = una entrada, como
 * Learning). Tabs por query param (?tab=):
 *  - "Nueva búsqueda": el Hiring Manager (roster TA, admins o líder con
 *    reportes directos — canIntake) abre y sigue SUS búsquedas.
 *  - "Búsquedas solicitadas": el repositorio del recruiter; misma audiencia que
 *    el dashboard de TA (canView). Un HM sin canView no ve esta tab.
 * El gate de la ruta es TaProtectedRoute (canView || canIntake). El bot del costado
 * SÍ está acá (Sol 29/09), con el avatar del asistente de TA, pero con el universo
 * recortado: responde solo por las búsquedas donde quien pregunta es el Hiring
 * Manager (columna Lead de la base). La base entera sigue siendo de TA.
 */
import { Navigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useRegisterTaContext, useTaAccess } from '@/hooks/useTalentAcquisition';
import { TaIntakeTab } from '@/components/talent-acquisition/TaIntakeTab';
import { TaIntakeRequestsTab } from '@/components/talent-acquisition/TaIntakeRequestsTab';

export function TaIntakePage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  // El bot del costado: el asistente de TA, pero acotado a las búsquedas de quien pregunta (Sol 29/09)
  useRegisterTaContext('intake');
  const { data: access } = useTaAccess();
  const canView = access?.canView ?? false;
  const tab = searchParams.get('tab') === 'solicitudes' && canView ? 'solicitudes' : 'intake';
  // Un Bar Raiser que no lidera a nadie entra a TA solo por sus guías (canInterview):
  // el intake le respondería FORBIDDEN (página vacía). Se lo manda a las guías.
  const soloGuias = !!access && !access.canIntake && access.canInterview;
  const setTab = (next: string) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'intake') params.delete('tab'); else params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  if (soloGuias) return <Navigate to="/people/talent-acquisition/interview" replace />;

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center gap-3">
        <Sparkles className="h-7 w-7 text-brand-green" />
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('talent_acquisition.intake.page_title')}</h1>
          <p className="text-sm text-muted-foreground">{t(canView ? 'talent_acquisition.intake.page_subtitle' : 'talent_acquisition.intake.subtitle_hm')}</p>
        </div>
      </div>
      {canView ? (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="intake">{t('talent_acquisition.intake.tab')}</TabsTrigger>
            <TabsTrigger value="solicitudes">{t('talent_acquisition.intake.requests.tab')}</TabsTrigger>
          </TabsList>
          <TabsContent value="intake" className="mt-4">
            <TaIntakeTab />
          </TabsContent>
          <TabsContent value="solicitudes" className="mt-4">
            <TaIntakeRequestsTab />
          </TabsContent>
        </Tabs>
      ) : (
        // Hiring Manager sin acceso al dashboard: solo sus búsquedas, sin barra de tabs
        <TaIntakeTab />
      )}
    </div>
  );
}
